// frontend/src/components/InteractiveMapPlanner/InteractiveMapPlanner.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Paper, Typography, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowRight, faUndo, faChevronLeft, faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import './InteractiveMapPlanner.scss';

import { useSnackbar } from '../../hooks/SnackbarProvider';
import { Activity, CustomPhoto, DraftPlan, PlannerMapPlace, PlannerLineData, getLatLng } from '../../types/interfaces';
import { usePlan } from '../../hooks/PlanProvider';
import { useTheme } from '../../hooks/ThemeProvider';
import { useMaps } from '../../hooks/MapProvider';
import { MAP_STYLES_LIGHT, MAP_STYLES_DARK } from '../Map/BaseMap';
import { DirectionsRequest } from '../../lib/maps_client';
import { PathSymbol } from '../../types/interfaces';
import { APIEndpoint } from 'src/lib/api_client';
import { LocationCard } from './LocationCard';
import { PlannerTimeline } from './PlannerTimeline';
import { keysToCamel } from 'src/lib/utils';
import { Button } from '../Button/Button';
import { PlaceLookupClient } from '../../lib/place_lookup_client';
import { AIAnalytics, PlanAnalytics } from '../../lib/analytics';

const DESKTOP_TIMELINE_PEEK_AMOUNT = 80; // Corresponds to $timeline-peek-amount in SCSS
const DEFAULT_MAP_PADDING = 50; // A general padding you want around your content

// Type for storing individual animated path segment data
interface AnimatedPathData {
  id: string; // Unique ID for the path segment, e.g., `originLocId-destLocId`
  originId: string;
  destinationId: string;
  pathCoordinates: google.maps.LatLngLiteral[];
  // Animation parameters from Path.tsx animationRef
  isRunning: boolean;
  offset: number;
  dotOpacity: number;
  glowIntensity: number;
  prevTime: number;
  symbolsCache: PathSymbol[] | null; // Cache for generated symbols
  polylineRef?: google.maps.Polyline; // Reference to the actual polyline on the map
}

type MarkerRefMap = Map<string, google.maps.Marker>;
type PolylineRefMap = Map<string, google.maps.Polyline>;

// Replicated from MapMarker.tsx for direct use
const MarkerIcon = (isUser: boolean): string => {
  const svg = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="12" fill="${isUser ? '#00FFFF' : 'rgba(0, 255, 255, 0.2)'}" />
        <circle cx="12" cy="12" r="6" fill="#00FFFF" />
      </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Helper to construct Google Places Photo URL
const getGooglePhotoUrl = (photoReference: string, maxWidth: number = 400): string => {
  return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${maxWidth}&photoreference=${photoReference}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
};

const InteractiveMapPlanner: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const timelineScrollAreaRef = useRef<HTMLDivElement>(null);
  const streamSuccessfullyCompletedRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const { mapsClient, isGoogleMapsLoaded, googleMapsLoadError } = useMaps();

  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);

  const [locations, setLocations] = useState<PlannerMapPlace[]>(JSON.parse(localStorage.getItem('aiPlannerLocations') || '[]') as PlannerMapPlace[]);
  const [lines, setLines] = useState<PlannerLineData[]>([]);
  const [animatedPathSegments, setAnimatedPathSegments] = useState<AnimatedPathData[]>([]); // Added state for animated paths
  const animationFrameIdRef = useRef<number>(0); // For the global animation loop

  const markersRef = useRef<MarkerRefMap>(new Map());
  const polylinesRef = useRef<PolylineRefMap>(new Map());

  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [isDesktopView, setIsDesktopView] = useState<boolean>(window.innerWidth > 768);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [promptText, setPromptText] = useState<string>('');

  const [isSaveNameDialogOpen, setIsSaveNameDialogOpen] = useState<boolean>(false);
  const [planNameForSave, setPlanNameForSave] = useState<string>('');

  const { triggerSnackbar: showSnackbar } = useSnackbar();
  const { setSelectedActivity, setIsActivityDrawerOpen, isActivityDrawerOpen: activityDrawerOpen, savePlan, selectedPlan, setSelectedPlan, setSelectedPlanActivities } = usePlan();
  const { isDarkMode } = useTheme();

  const clearMapObjects = useCallback(() => {
    markersRef.current.forEach(marker => {
      if (marker instanceof google.maps.Marker) {
        marker.setMap(null);
      }
    });
    markersRef.current.clear();
    polylinesRef.current.forEach(polyline => polyline.setMap(null));
    polylinesRef.current.clear();
  }, []);

  const handleLocationSelect = useCallback((locationData: PlannerMapPlace) => {
    // Track AI location selection
    AIAnalytics.locationSelect(locationData.name || 'Unknown Location', locationData.sequence || 0);
    
    setActiveLocationId(locationData.placeId);
    if (googleMap && locationData.position) googleMap.panTo(locationData.position);

    const coordinates = getLatLng(locationData);
    const activityForDrawer: Partial<Activity> & { id: string } = {
      id: locationData.placeId,
      name: locationData.name,
      description: locationData.description,
      notes: locationData.notes || locationData.description,
      geometry: {
        location: new google.maps.LatLng(coordinates.lat, coordinates.lng)
      },
      formatted_address: locationData.formatted_address || `Lat: ${coordinates.lat.toFixed(4)}, Lng: ${coordinates.lng.toFixed(4)}`,
      photos: locationData.photos || [],
      rating: locationData.rating,
      website: locationData.website,
      phone_number: locationData.phone_number,
      placeId: locationData.placeId,
      type: 'ai_suggestion',
    };
    setSelectedActivity(activityForDrawer as Activity);
    setIsActivityDrawerOpen(true);

    const cardIndex = locations.findIndex(loc => loc.placeId === locationData.placeId);
    if (cardIndex !== -1 && cardContainerRef.current) {
      const cardElement = cardContainerRef.current.children[cardIndex] as HTMLElement;
      if (cardElement) cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [googleMap, setSelectedActivity, setIsActivityDrawerOpen, locations]);

  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapContainerRef.current) return;

    // Create new map instance if it doesn't exist and we have a container
    if (!googleMap) {
      const mapInstance = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: 40.7163, lng: -73.9859 }, // Default to Lower East Side, NYC
        zoom: 13,
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: isDarkMode ? MAP_STYLES_DARK : MAP_STYLES_LIGHT,
      });
      setGoogleMap(mapInstance);
      setBounds(new window.google.maps.LatLngBounds());
    } else {
      googleMap.setOptions({
        styles: isDarkMode ? MAP_STYLES_DARK : MAP_STYLES_LIGHT,
      });
    }
  }, [isGoogleMapsLoaded, googleMap, isDarkMode]);

  // Effect to handle window resize for responsive search bar
  useEffect(() => {
    const handleResize = () => {
      setIsDesktopView(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    // Call handler right away so state is correct initial non-SSR/CSR conditions
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const streamInteractiveMapPlan = (prompt: string): EventSource => {
    const queryParams = new URLSearchParams({ prompt });
    let url = `${APIEndpoint}/ai/interactive-map-stream/?${queryParams.toString()}`;
    return new EventSource(url, { withCredentials: true });
  }

  const handleSendPrompt = useCallback(async () => {
    if (!window.google || !window.google.maps) {
      setErrorMsg("Map API is not loaded yet. Please wait a moment and try again.");
      showSnackbar("Map API is not loaded yet. Please wait a moment and try again.", 5000, "error");
      setIsLoading(false);
      setIsStreaming(false);
      return;
    }
    if (!promptText.trim()) return;
    
    // Track AI prompt submission
    const promptStartTime = Date.now();
    AIAnalytics.promptSubmit(promptText, 'Current Location');
    
    setIsLoading(true); setIsStreaming(true); setErrorMsg('');
    clearMapObjects(); setLocations([]); setLines([]); setActiveLocationId(null);
    streamSuccessfullyCompletedRef.current = false;

    const initialBounds = new window.google.maps.LatLngBounds();
    setBounds(initialBounds); // Reset bounds

    const currentEventSource = streamInteractiveMapPlan(promptText);
    let generatedLocationsCount = 0;
    
    currentEventSource.onmessage = (event) => {
      const parsedData = keysToCamel(JSON.parse(event.data));

      // Use a new bounds instance for each message processing to avoid issues with async state updates
      const currentMessageBounds = new window.google.maps.LatLngBounds();

      if (parsedData.type === 'location') {
        const locArgs = parsedData.data;
        if (!locArgs.placeId) {
          console.warn("Received location without placeId, skipping:", locArgs.name);
          return;
        }

        const mappedPhotos: CustomPhoto[] = (locArgs.photoReferences || []).map((ref: string, index: number) => ({
          id: uuidv4(),
          url: getGooglePhotoUrl(ref),
          photoReference: ref,
          height: 400, // Default height
          width: 400, // Default width
          attribution: (locArgs.htmlAttributions && locArgs.htmlAttributions[index]) ? locArgs.htmlAttributions[index] : undefined,
        }));

        const newLocation: PlannerMapPlace = {
          placeId: locArgs.placeId,
          name: locArgs.name,
          description: locArgs.description,
          geometry: {
            location: new google.maps.LatLng(Number(locArgs.lat), Number(locArgs.lng))
          },
          time: locArgs.time,
          duration: locArgs.duration,
          sequence: locArgs.sequence,
          formatted_address: locArgs.address || '',
          photos: mappedPhotos,
          htmlAttributions: locArgs.htmlAttributions || [],
          rating: locArgs.rating,
          website: locArgs.website,
          phone_number: locArgs.phone_number,
          notes: locArgs.notes,
          position: new window.google.maps.LatLng(Number(locArgs.lat), Number(locArgs.lng)),
        };
        setLocations(prev => {
          if (prev.find(p => p.placeId === newLocation.placeId)) return prev; // Avoid duplicates
          const updatedLocations = [...prev, newLocation];
          updatedLocations.forEach(loc => currentMessageBounds.extend(loc.position!));
          generatedLocationsCount = updatedLocations.length;
          // localStorage.setItem('aiPlannerLocations', JSON.stringify(updatedLocations));
          return updatedLocations;
        });
        // No direct setBounds here, accumulate in currentMessageBounds then update via setBounds(prev => prev.union(currentMessageBounds))
      } else if (parsedData.type === 'line') {
        const lineArgs = parsedData.data;
        const newLine: PlannerLineData = {
          id: lineArgs.id || uuidv4(),
          name: lineArgs.name,
          start_location_id: lineArgs.start_location_id,
          end_location_id: lineArgs.end_location_id,
          transport: lineArgs.transport_mode || lineArgs.transport,
          travelTime: lineArgs.travel_time || lineArgs.travelTime,
        };
        setLines(prev => [...prev, newLine]);
      } else if (parsedData.type === 'error') {
        setErrorMsg(parsedData.message || 'An unknown error occurred during streaming.');
        setIsLoading(false); setIsStreaming(false);
        currentEventSource.close();
        
        // Track AI planning error
        const timeTaken = Date.now() - promptStartTime;
        AIAnalytics.planningComplete(generatedLocationsCount, timeTaken, false);
        
        showSnackbar(parsedData.message || 'Streaming error.', 5000, 'error');
      } else if (parsedData.type === 'done') {
        setIsLoading(false); setIsStreaming(false);
        streamSuccessfullyCompletedRef.current = true;
        currentEventSource.close();
        
        // Track AI planning completion
        const timeTaken = Date.now() - promptStartTime;
        AIAnalytics.planningComplete(generatedLocationsCount, timeTaken, true);
        
        showSnackbar('Plan generation complete!', 3000, 'success');
      }

      // After processing a location, update the main bounds state
      if (parsedData.type === 'location') {
        setBounds(prevGlobalBounds => {
          const newGlobalBounds = new window.google.maps.LatLngBounds(prevGlobalBounds?.getSouthWest(), prevGlobalBounds?.getNorthEast());
          newGlobalBounds.union(currentMessageBounds);
          return newGlobalBounds;
        });
      }
    };
    currentEventSource.onerror = (errorEvent) => {
      // The 'event' target is the EventSource instance itself
      const es = errorEvent.target as EventSource;

      if (streamSuccessfullyCompletedRef.current && es.readyState === EventSource.CLOSED) {
        // If 'done' was processed AND the connection is now closed (readyState === 2),
        // assume this 'error' event is due to the normal connection teardown
        // (either by server or by our client.close() call in the 'done' handler).
        console.log("EventSource connection closed after successful stream completion.", errorEvent);
        setIsLoading(false); // Ensure UI consistency
        setIsStreaming(false);
        // No user-facing error message needed here.
      } else {
        // If 'done' was NOT processed, or if it's an error while still 'OPEN' or 'CONNECTING',
        // then it's a genuine error during the streaming process.
        console.error("EventSource failed during stream:", errorEvent, "Ready state:", es.readyState);
        setErrorMsg('Failed to connect to the streaming service or stream interrupted.');
        setIsLoading(false);
        setIsStreaming(false);
        showSnackbar('Connection error during streaming.', 5000, 'error');
      }

      // EventSource.close() might have already been called if 'done' was received and processed.
      // Calling it again on a closed source is a no-op and safe.
      // If it wasn't closed (genuine error before 'done'), this ensures it is.
      if (es.readyState !== EventSource.CLOSED) {
        es.close();
      }
    };
  }, [promptText, showSnackbar, clearMapObjects]);

  const handleReset = useCallback(() => {
    // Track AI plan reset
    AIAnalytics.trackEvent('ai_plan_reset', {
      event_category: 'ai_planner',
      locations_count: locations.length,
      event_label: 'plan_reset'
    });
    
    setPromptText('');
    setLocations([]);
    setLines([]);
    setActiveLocationId(null);
    setErrorMsg('');
    setIsLoading(false);
    setIsStreaming(false);
    clearMapObjects();
    if (googleMap) {
      const defaultCenter = { lat: 34.0522, lng: -118.2437 };
      googleMap.setCenter(defaultCenter);
      googleMap.setZoom(10);
      setBounds(new window.google.maps.LatLngBounds(new google.maps.LatLng(defaultCenter), new google.maps.LatLng(defaultCenter)));
    }
    if (activityDrawerOpen) setIsActivityDrawerOpen(false);
    setShowTimeline(false);
  }, [googleMap, clearMapObjects, activityDrawerOpen, setIsActivityDrawerOpen]);

  // Effect for creating/updating markers
  useEffect(() => {
    if (!googleMap || !window.google || !window.google.maps) return;

    const newMarkers: MarkerRefMap = new Map();
    locations.forEach(location => {
      if (markersRef.current.has(location.placeId)) {
        const existingMarker = markersRef.current.get(location.placeId)!;
        newMarkers.set(location.placeId, existingMarker);
        markersRef.current.delete(location.placeId);
      } else {
        // Create new google.maps.Marker with MapMarker.tsx style
        const marker = new window.google.maps.Marker({
          position: location.position,
          map: googleMap,
          title: location.name,
          icon: {
            url: MarkerIcon(false), // false for AI-generated points
            scaledSize: new google.maps.Size(24, 24),
            anchor: new google.maps.Point(12, 12),
          }
        });
        marker.addListener('click', () => handleLocationSelect(location));
        newMarkers.set(location.placeId, marker);
      }
    });

    // Remove old markers that are no longer in locations
    markersRef.current.forEach(marker => {
      // All markers managed by this effect should now be google.maps.Marker
      if (marker instanceof google.maps.Marker) {
        marker.setMap(null);
      } else { // Handle cases where old AdvancedMarkerElement might still exist from previous renders
        // This branch should ideally not be hit if all markers become google.maps.Marker
        // For safety during transition:
        // @ts-ignore
        if (marker.map) marker.map = null; else marker.setMap(null);
      }
    });
    markersRef.current = newMarkers;

  }, [locations, googleMap, activeLocationId, handleLocationSelect]);

  // Effect for creating/updating polylines
  useEffect(() => {
    if (!googleMap || !isGoogleMapsLoaded || !mapsClient) return;
    const newAnimatedSegments: AnimatedPathData[] = [];

    const processLocationPairs = async () => {
      // Ensure mapsClient is definitely available here after async/await points
      if (!mapsClient) {
        console.error("MapsClient not available in processLocationPairs");
        return;
      }

      // Clear existing polylines from map before redrawing AI paths
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current.clear();

      for (let i = 0; i < locations.length - 1; i++) {
        const originLoc = locations[i];
        const destLoc = locations[i + 1];
        const segmentId = `${originLoc.placeId}-${destLoc.placeId}`;

        let pathCoordinates: google.maps.LatLngLiteral[] = [];

        try {
          const request: DirectionsRequest = {
            origin: getLatLng(originLoc),
            destination: getLatLng(destLoc),
            travelMode: google.maps.TravelMode.WALKING, // Default to walking, can be made configurable
          };
          if (!mapsClient) throw new Error("Maps client disappeared");
          const result = await mapsClient.directions(request);
          if (result.routes[0]?.overview_path) {
            pathCoordinates = result.routes[0].overview_path.map((p: google.maps.LatLng | google.maps.LatLngLiteral) => {
              if (typeof (p as google.maps.LatLng).toJSON === 'function') {
                return (p as google.maps.LatLng).toJSON();
              }
              return p as google.maps.LatLngLiteral;
            });
          }
        } catch (error) {
          console.error(`Error fetching directions from ${originLoc.name} to ${destLoc.name}:`, error);
          const originJson = originLoc.position && typeof originLoc.position.toJSON === 'function' ? originLoc.position.toJSON() : getLatLng(originLoc);
          const destJson = destLoc.position && typeof destLoc.position.toJSON === 'function' ? destLoc.position.toJSON() : getLatLng(destLoc);
          pathCoordinates = [originJson, destJson];
        }

        // Find existing animation state or create new
        const existingSegmentState = animatedPathSegments.find(s => s.id === segmentId);

        const currentSegmentData: AnimatedPathData = {
          id: segmentId,
          originId: originLoc.placeId,
          destinationId: destLoc.placeId,
          pathCoordinates: pathCoordinates,
          isRunning: true, // Start animation by default
          offset: existingSegmentState?.offset || 0,
          dotOpacity: existingSegmentState?.dotOpacity || 0, // Start invisible, fade in
          glowIntensity: existingSegmentState?.glowIntensity || 0,
          prevTime: existingSegmentState?.prevTime || performance.now(),
          symbolsCache: existingSegmentState?.symbolsCache || null,
        };

        const polylineOptions: google.maps.PolylineOptions = {
          path: pathCoordinates,
          geodesic: true,
          // Styling from Path.tsx (isActive=true, isHovered=false equivalent)
          strokeColor: 'rgb(255, 91, 165)',
          strokeOpacity: 0.8,
          strokeWeight: 5,
          zIndex: 2, // Higher zIndex for active paths
          // icons: [] // Animation symbols will be set by the animation loop
        };

        const polyline = new window.google.maps.Polyline(polylineOptions);
        polyline.setMap(googleMap);
        polylinesRef.current.set(segmentId, polyline);
        currentSegmentData.polylineRef = polyline; // Store ref for animation loop
        newAnimatedSegments.push(currentSegmentData);
      }
      setAnimatedPathSegments(newAnimatedSegments);
    };

    if (locations.length > 1) {
      processLocationPairs();
    } else {
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current.clear();
      setAnimatedPathSegments([]);
    }
  }, [locations, googleMap, isGoogleMapsLoaded, mapsClient]);

  // Placeholder for animation logic (to be filled in next step)
  const createAiPathSymbols = useCallback((segment: AnimatedPathData): PathSymbol[] => {
    if (!segment.isRunning || !window.google || !window.google.maps) return []; // Added map check

    // Logic adapted from Path.tsx createPathSymbols
    const { offset, dotOpacity, glowIntensity } = segment;
    const baseGlowIntensity = 0.3 + (glowIntensity * 0.4);

    const symbols: PathSymbol[] = [
      {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 4,
          fillColor: '#FFFFF0', // Ivory/White dot
          fillOpacity: 0.95 * dotOpacity,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        offset: `${offset}%`,
      },
      {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8 + (glowIntensity * 4), // Glow effect
          fillColor: '#00FFFF', // Cyan glow
          fillOpacity: baseGlowIntensity * dotOpacity,
          strokeWeight: 0,
        },
        offset: `${offset}%`,
      },
      {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12 + (glowIntensity * 6), // Wider glow effect
          fillColor: '#00FFFF',
          fillOpacity: baseGlowIntensity * 0.5 * dotOpacity,
          strokeWeight: 0,
        },
        offset: `${offset}%`,
      }
    ];

    // Trail effect from Path.tsx
    const trailLength = 8; // Number of trail dots
    for (let i = 0; i < trailLength; i++) {
      const fadeOffset = i / trailLength;
      // Make trail dots slightly dimmer and more transparent as they get further from the main dot
      const trailOpacity = (0.3 - (fadeOffset * 0.25)) * (1 + glowIntensity * 0.5) * dotOpacity;
      const trailScale = Math.max(1, 3 - (fadeOffset * 2) + (glowIntensity * 2));
      const trailActualOffset = offset - (i * 3 * (1 + glowIntensity * 0.2)); // Adjust spacing based on glow

      if (trailActualOffset >= 0) { // Only add trail dots that are not behind the start
        symbols.push({
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: trailScale,
            fillColor: '#00FFFF', // Cyan trail
            fillOpacity: Math.max(0, trailOpacity), // Ensure opacity isn't negative
            strokeWeight: 0,
          },
          offset: `${trailActualOffset}%`,
        });
      }
    }
    return symbols;
  }, []);

  const runAnimationLoop = useCallback(() => {
    if (!googleMap) {
      animationFrameIdRef.current = 0;
      return;
    }

    let hasActiveAnimations = false;
    const updatedSegments = animatedPathSegments.map(segment => {
      if (!segment.isRunning) return segment;
      hasActiveAnimations = true;

      const currentTime = performance.now();
      const prevTime = segment.prevTime || currentTime;
      const deltaTime = (currentTime - prevTime) / 1000;

      // Animation logic from Path.tsx animateFrame
      const animationSpeedFactor = 20; // Lower is faster; 100 / (seconds for full loop)
      const increment = (100 / (100 / animationSpeedFactor)) * deltaTime; // e.g. 20% per second if animationSpeedFactor is 20

      let newOffset = (segment.offset + increment);
      if (newOffset >= 100) {
        newOffset = 0; // Reset to loop
      }

      let newDotOpacity = 1;
      // Fade in at start, fade out at end of the 0-100% offset cycle
      const fadeDuration = 5; // Percentage of offset for fade in/out
      if (newOffset < fadeDuration) {
        newDotOpacity = newOffset / fadeDuration;
      } else if (newOffset > (100 - fadeDuration)) {
        newDotOpacity = (100 - newOffset) / fadeDuration;
      }
      newDotOpacity = Math.max(0, Math.min(1, newDotOpacity)); // Clamp opacity between 0 and 1

      // Glow intensity based on velocity (how much offset changes per frame)
      // This gives a subtle pulse effect as the dot moves
      const velocity = Math.min(100, increment / Math.max(0.001, deltaTime)); // Percentage per second
      const targetGlow = Math.min(1, velocity * 0.02); // Adjust multiplier for desired glow sensitivity
      const newGlowIntensity = segment.glowIntensity + (targetGlow - segment.glowIntensity) * 0.1; // Smooth transition to target glow

      const updatedSegment = {
        ...segment,
        offset: newOffset,
        dotOpacity: newDotOpacity,
        glowIntensity: newGlowIntensity,
        prevTime: currentTime,
        symbolsCache: null, // Invalidate symbol cache as parameters changed
      };

      if (updatedSegment.polylineRef) {
        const symbols = createAiPathSymbols(updatedSegment);
        updatedSegment.polylineRef.setOptions({ icons: symbols });
      }
      return updatedSegment;
    });

    setAnimatedPathSegments(updatedSegments);

    if (hasActiveAnimations) {
      animationFrameIdRef.current = requestAnimationFrame(runAnimationLoop);
    } else {
      animationFrameIdRef.current = 0; // Stop loop if no active animations
    }
  }, [animatedPathSegments, googleMap, createAiPathSymbols]);

  useEffect(() => {
    // Start/stop global animation loop based on whether there are segments to animate
    if (animatedPathSegments.some(s => s.isRunning) && !animationFrameIdRef.current && googleMap) {
      animationFrameIdRef.current = requestAnimationFrame(runAnimationLoop);
    }
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = 0;
      }
    };
  }, [animatedPathSegments, googleMap, runAnimationLoop]);

  // Effect to fit map to bounds - REFINED with asymmetrical padding
  useEffect(() => {
    if (googleMap && bounds && (locations.length > 0 || lines.length > 0)) {
      if (!bounds.isEmpty()) {
        let paddingOptions: google.maps.Padding | number;

        // Determine if the timeline is currently peeking on a desktop view
        const isTimelinePeekingOnDesktop =
          isDesktopView &&
          (showTimeline || locations.length > 0); // Matches your PlannerTimeline visibility logic for desktop

        if (isTimelinePeekingOnDesktop) {
          // Apply asymmetrical padding if the timeline is peeking on desktop
          paddingOptions = {
            top: DEFAULT_MAP_PADDING,
            bottom: DEFAULT_MAP_PADDING,
            left: DEFAULT_MAP_PADDING,
            right: DEFAULT_MAP_PADDING + DESKTOP_TIMELINE_PEEK_AMOUNT, // Add space for the peeking panel
          };
        } else {
          // Apply default symmetrical padding otherwise (panel closed or mobile view)
          paddingOptions = DEFAULT_MAP_PADDING;
        }

        googleMap.fitBounds(bounds, paddingOptions);
      }
    }
    // CRITICAL: Add dependencies that affect the 'isTimelinePeekingOnDesktop' calculation
  }, [googleMap, bounds, locations.length, lines.length, isDesktopView, showTimeline]);

  const handleCardNavigation = (direction: number) => {
    if (locations.length === 0) return;
    const currentIndex = locations.findIndex(loc => loc.placeId === activeLocationId);
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = 0;
    if (nextIndex >= locations.length) nextIndex = locations.length - 1;
    if (locations[nextIndex]) {
      handleLocationSelect(locations[nextIndex]);
    }
  };

  const handleExportPlan = useCallback(() => {
    if (locations.length === 0) {
      showSnackbar("No plan to export.", 3000, "warning");
      return;
    }
    let planText = "Mindara Day Plan:\n\n";
    const sortedLocations = [...locations].sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity));

    sortedLocations.forEach((loc, index) => {
      planText += (loc.sequence || (index + 1)) + ". " + loc.name + "\n";
      if (loc.time) planText += "   Time: " + loc.time + "\n";
      if (loc.duration) planText += "   Duration: " + loc.duration + "\n";
      planText += "   Details: " + loc.description + "\n\n";

      if (index < sortedLocations.length - 1) {
        const nextLoc = sortedLocations[index + 1];
        const line = lines.find(l => l.start_location_id === loc.placeId && l.end_location_id === nextLoc.placeId);
        if (line) {
          planText += "   -> Travel to " + nextLoc.name + " via " + (line.transport || 'route');
          if (line.travelTime) planText += " (" + line.travelTime + ")";
          planText += "\n   " + (line.name || '') + "\n\n";
        }
      }
    });

    const blob = new Blob([planText], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'mindara_plan.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar("Plan exported!", 3000, "success");
  }, [locations, lines, showSnackbar]);

  const handleSaveAiPlan = useCallback(async () => {
    if (locations.length === 0) {
      showSnackbar("No plan to save.", 3000, "warning");
      return;
    }
    if (locations.length > 3) {
      showSnackbar("Day plans can only have up to 3 activities. This plan has " + locations.length + ".", 5000, "error");
      return;
    }
    // Set initial name for the dialog
    const initialPlanName = `AI Plan: ${promptText.substring(0, 20)}${promptText.length > 20 ? '...' : ''}` || "My AI Generated Day Plan";
    setPlanNameForSave(initialPlanName);
    setIsSaveNameDialogOpen(true);

  }, [locations, promptText, showSnackbar]);

  const executeActualSave = useCallback(async (nameOfPlanToSave: string) => {
    const sortedLocations = [...locations].sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity));

    try {
      setIsLoading(true);

      // Enrich AI places with database data (including Resy IDs)
      const enrichedPlaces = await PlaceLookupClient.enrichAIPlaces(sortedLocations);

      const activitiesForPlan: Activity[] = enrichedPlaces.map(loc => ({
        ...loc, // Spread the enriched place data
        placeId: loc.placeId, // Ensure placeId is set
        description: loc.description,
        notes: loc.notes || loc.description,
        order: loc.sequence || 0,
        type: 'ai_suggestion',
        // Include Resy ID if found in database
        resyId: loc.resyId,
      }));

      // CRITICAL: Update the selected plan's activities with enriched data
      // This ensures the frontend UI reflects the enriched place data with correct resy_ids
      setSelectedPlanActivities(activitiesForPlan);

      console.log('🎯 [InteractiveMapPlanner] Updated selectedPlan activities with enriched data:', {
        activitiesCount: activitiesForPlan.length,
        withResyIds: activitiesForPlan.filter(a => a.resyId).length,
        resyIds: activitiesForPlan.map(a => ({ name: a.name, resyId: a.resyId })).filter(a => a.resyId)
      });

      const draftPlanToSave: DraftPlan = {
        name: nameOfPlanToSave.trim() === '' ? "Untitled AI Plan" : nameOfPlanToSave.trim(),
        activities: activitiesForPlan,
        isPublic: false,
      };

      // The savePlan from PlanProvider will internally call setSelectedPlan on successful save of a new plan
      // or update of an existing one if it returns the saved plan object to its internal mutation's onSuccess.
      // Let's assume savePlan updates the context correctly.
      await savePlan(draftPlanToSave);
      
      // Track AI plan save
      PlanAnalytics.save('ai_generated', draftPlanToSave.activities.length, 'ai_generated');

      // It is crucial that `savePlan` ensures `selectedPlan` in the context is updated
      // with the *full SavedPlan object including its ID* before navigation.
      // If savePlan is Promise<void>, we might need to refetch or have savePlan return the SavedPlan.
      // For now, relying on PlanProvider's internal setSelectedPlan call via its mutation.

      showSnackbar(`Plan "${draftPlanToSave.name}" saved successfully!`, 3000, "success");

      // Post-save: Navigate to map/builder. 
      // The `selectedPlan` in context should now be the newly saved plan (done by PlanProvider).
      navigate('/map'); // Or '/builder' if that's the specific route for the main planner UI
      setShowTimeline(true); // Ensure timeline/planner UI is visible if on /map

    } catch (error) {
      console.error("Failed to save AI plan:", error);
      showSnackbar("Failed to save AI plan. Check console for details.", 5000, "error");
    } finally {
      setIsLoading(false);
      setIsSaveNameDialogOpen(false); // Close dialog regardless of outcome
    }
  }, [locations, savePlan, showSnackbar, setIsLoading, navigate, setSelectedPlan, setSelectedPlanActivities, promptText]);

  const handleDialogSaveName = () => {
    executeActualSave(planNameForSave);
  };

  const handleDialogClose = () => {
    setIsSaveNameDialogOpen(false);
  };

  const sortedDayPlanItinerary = [...locations].sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity) || (a.time || '').localeCompare(b.time || ''));

  useEffect(() => {
    if (locations.length > 0) {
      try {
        localStorage.setItem('aiPlannerLocations', JSON.stringify(locations));
      } catch (error) {
        console.error("Failed to save locations to local storage:", error);
      }
    } else {
      localStorage.removeItem('aiPlannerLocations');
    }
  }, [locations]);

  if (googleMapsLoadError) {
    return <Typography color="error">Error loading Google Maps: {googleMapsLoadError.message}</Typography>;
  }
  if (!isGoogleMapsLoaded) {
    return <Box className="global-spinner-overlay"><CircularProgress /></Box>;
  }

  return (
    <Box className="interactive-planner-container">
      <Box
        ref={mapContainerRef}
        id="interactive-map-instance"
        className={`map-area ${showTimeline && isDesktopView ? 'map-area-shifted' : ''}`}
      />

      <Box
        className={[
          'search-overlay',
          showTimeline && isDesktopView ? 'search-overlay-shifted-left' : '',
          showTimeline && !isDesktopView ? 'search-overlay-hidden-mobile' : ''
        ].filter(Boolean).join(' ').trim()}
      >
        <Paper component="form" className="search-bar-paper" onSubmit={(e) => { e.preventDefault(); handleSendPrompt(); }}>
          <IconButton sx={{ p: '10px' }} aria-label="search-icon" type="submit"> <FontAwesomeIcon icon={faSearch} /> </IconButton>
          <TextField
            fullWidth variant="standard"
            placeholder="e.g., vibey cocktails and omakase date in LES"
            value={promptText} onChange={(e) => setPromptText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendPrompt(); } }}
            InputProps={{ disableUnderline: true }} multiline maxRows={3} sx={{ overflowY: 'auto' }}
          />
          <IconButton color="primary" sx={{ p: '10px' }} aria-label="generate" onClick={handleSendPrompt} disabled={isLoading || isStreaming}>
            {(isLoading || isStreaming) ? <CircularProgress size={24} /> : <FontAwesomeIcon icon={faArrowRight} />}
          </IconButton>
        </Paper>
      </Box>

      {locations.length > 0 && (
        <Box className={`card-carousel-overlay ${showTimeline && isDesktopView ? 'card-carousel-shifted' : ''}`}>
          <Box ref={cardContainerRef} className="location-cards-container">
            {locations.map((loc) => (
              <LocationCard
                key={loc.placeId}
                location={loc}
                isActive={loc.placeId === activeLocationId}
                onClick={() => handleLocationSelect(loc)}
              />
            ))}
          </Box>
          <Box className="carousel-navigation">
            <IconButton onClick={() => handleCardNavigation(-1)} disabled={locations.findIndex(l => l.placeId === activeLocationId) <= 0 || locations.length === 0}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </IconButton>
            <Box className="carousel-dots">
              {locations.map((loc) => (<Box key={loc.placeId} className={`dot ${loc.placeId === activeLocationId ? 'active' : ''}`} />))}
            </Box>
            <IconButton onClick={() => handleCardNavigation(1)} disabled={locations.findIndex(l => l.placeId === activeLocationId) >= locations.length - 1 || locations.length === 0}>
              <FontAwesomeIcon icon={faChevronRight} />
            </IconButton>
          </Box>
        </Box>
      )}

      <IconButton onClick={handleReset} className="reset-map-button" disabled={isLoading || isStreaming} title="Reset Map & Plan">
        <FontAwesomeIcon icon={faUndo} />
      </IconButton>

      <IconButton
        className={`timeline-mobile-toggle ${showTimeline ? 'timeline-is-open' : ''}`}
        onClick={() => setShowTimeline(prev => !prev)}
        sx={{
          display: 'flex',
          position: 'absolute',
          zIndex: 1001,
        }}
        title={showTimeline ? "Hide Plan" : "Show Plan"}
      >
        {showTimeline ? <FontAwesomeIcon icon={faTimes} /> : <EventNoteIcon />}
      </IconButton>

      {showTimeline && !isDesktopView && <Box className="map-dim-overlay visible" onClick={() => setShowTimeline(false)} />}

      {(isLoading && !isStreaming) && (
        <Box className="global-spinner-overlay"> <CircularProgress /> </Box>
      )}

      <Dialog open={isSaveNameDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Save Your AI-Generated Plan</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="planName"
            label="Plan Name"
            type="text"
            fullWidth
            variant="standard"
            value={planNameForSave}
            onChange={(e) => setPlanNameForSave(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleDialogSaveName();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} intent="neutral">Cancel</Button>
          <Button onClick={handleDialogSaveName} intent="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {(showTimeline || (isDesktopView && locations.length > 0)) && (
        <PlannerTimeline
          isVisible={showTimeline || (isDesktopView && locations.length > 0)}
          itineraryItems={sortedDayPlanItinerary}
          connectingLines={lines}
          activeItemId={activeLocationId}
          onClose={() => setShowTimeline(false)}
          onExport={handleExportPlan}
          onSavePlan={handleSaveAiPlan}
          onItemClick={(item) => handleLocationSelect(item)}
          timelineScrollAreaRef={timelineScrollAreaRef}
        />
      )}

    </Box>
  );
};

export default InteractiveMapPlanner;
